import Component, { ComponentAttachParams, ComponentUpdateParams } from '../Component/Component';
import { Random } from '../Utility/Random';
import Scene from '../Scene/Scene';
import TransformComponent from '../Component/Transform';
import { Constructor } from '../Utility/Types';
import { ArrayHelper } from 'Utility/ArrayHelper';
import { MathHelper } from 'Utility/MathHelper';

export interface IGameObjectCreateParams {
    [key: string]: unknown;
}
export enum GameObjectEvent {
    DESTROYED = 'destroyed',
    CHILD_ADDED = 'childAdded',
    CHILD_REMOVED = 'childRemoved',
    POINTER_DOWN = 'pointerDown',
    POINTER_OUT = 'pointerOut',
    POINTER_OVER = 'pointerOver',
    POINTER_UP = 'pointerUp',
    POINTER_UP_OUTSIDE = 'pointerUpOutside',
    SELECTED = 'selected',
    POINTER_MOVE = 'pointerMove',
    TOGGLED = 'toggled',
    ADDED = 'added',
    DISPLAY_PARENT_CHANGE = 'displayParentChange',
}

const GameObjectsByID: Map<string, GameObject> = new Map<string, GameObject>();
const GameObjectsByName: Map<string, GameObject[]> = new Map<
    string,
    GameObject[]
    >();

interface IPrototype<T extends GameObject> {
    new (...args: any[]): T;
    //create(): void;
}

export function registerGameObject(gameObject: GameObject) {
    // 'By ID' lookup Map.
    GameObjectsByID.set(gameObject.id, gameObject);

    // 'By Name' lookup Map - arrays allow for multiple items per name.
    if (gameObject.name && typeof gameObject.name === 'string' && gameObject.name !== '') {
        let gameObjectsByName = GameObjectsByName.get(gameObject.name);
        if (!gameObjectsByName) {
            gameObjectsByName = [];
            GameObjectsByName.set(gameObject.name, gameObjectsByName);
        }
        gameObjectsByName.push(gameObject);
    }
}

export function unregisterGameObject(gameObject: GameObject) {
    GameObjectsByID.delete(gameObject.id);
    const gameObjectsByName = GameObjectsByName.get(gameObject.name);
    if (gameObjectsByName) {
        if (gameObjectsByName.length > 1) {
            GameObjectsByName.set(
                gameObject.name,
                gameObjectsByName.filter(go => go !== gameObject)
            );
        } else {
            GameObjectsByName.delete(gameObject.name);
        }
    }
}

export default abstract class GameObject<
    T extends PIXI.Container = PIXI.Container
    > {
    public readonly id: string;
    public name: string;
    public components: Component[];

    public parent: GameObject;
    public children: GameObject[];

    public destroyed: boolean = false;

    protected componentTypeMap: Map<{ new (): Component }, Component>;

    /**
     * PIXI object container.
     */
    public container: T;

    /**
     * @deprecated
     * Containing scene reference
     * TODO: Decouple this from Gameobject.
     */
    public scene: Scene;

    public layoutData: unknown;

    // TODO: Replace any with IGameObjectCreateParams
    public static Instantiate<T extends GameObject>(
        ctor: IPrototype<T>,
        params?: any
    ): T {
        const gameObject: T = new ctor(params ? params.id : null);
        gameObject.create(params || {});

        registerGameObject(gameObject);

        return gameObject;
    }

    public static findByID(id: string): GameObject {
        return GameObjectsByID.get(id);
    }

    public static findByName(name: string): GameObject[] {
        return GameObjectsByName.get(name);
    }

    public static findFirstByName(name: string): GameObject {
        const matchingGameObjects: GameObject[] = GameObject.findByName(name);

        return matchingGameObjects
            ? matchingGameObjects.find(
                (gameObject: GameObject) => name === gameObject.name
            )
            : undefined;
    }

    constructor(id?: string) {
        if (id) {
            this.id = id;
        } else {
            this.id = `${this.constructor.name}_${Random.ID()}`;
        }

        this.components = [];
        this.children = [];
        this.componentTypeMap = new Map<{ new (): Component }, Component>();
    }


    protected create(params: any): void {
        if (params.name) {
            this.name = params.name;
        }

        this.instantiateContainer(params);

        if (this.container) {
            this.container.name = (this.name ? this.name + '_' : '') + this.id;
            (this.container as any).gameObject = this;
        }

        this.onCreate(params);
    }

    protected abstract onCreate(params: any): void;

    protected instantiateContainer(params: any): void {
        this.container = new PIXI.Container() as T;
    }

    public destroy(): void {
        if (this.destroyed) {
            console.warn('GameObject already destroyed.');
            return;
        }

        this.emit(GameObjectEvent.DESTROYED, this);

        unregisterGameObject(this);

        if (this.parent) {
            this.parent.removeChild(this);
        }

        this.container.destroy();

        this.destroyed = true;

        while (this.children.length > 0) {
            this.children[0].destroy();
        }

        this.removeAllComponents();
    }

    public getChildByName(name: string): GameObject {
        return this.children.find(c => c.name === name);
    }

    public addChild(gameObject: GameObject): void {
        // Check if re-parenting a gameobject. Update transforms.
        if (
            gameObject.parent &&
            gameObject.parent.hasChild(gameObject) &&
            gameObject.parent !== this
        ) {
            // Always maintain 'global' position when re-parenting.
            let position = gameObject.parent.container.toGlobal(
                gameObject.container.position
            );
            position = this.container.toLocal(position);

            const transform = gameObject.getComponent(TransformComponent);
            if (transform) {
                transform.position.set(position.x, position.y);
            }

            // Remove from previous parent.
            const index = gameObject.parent.children.indexOf(gameObject);
            if (index >= 0) gameObject.parent.children.splice(index, 1);
        }

        gameObject.parent = this;

        if (!this.hasChild(gameObject)) {
            this.children.push(gameObject);
        }

        this.container.addChild(gameObject.container);

        this.emit(GameObjectEvent.CHILD_ADDED, gameObject);
        gameObject.emit(GameObjectEvent.ADDED);
    }
    /**
     * Add child at index
     */
    public addChildAt(gameObject: GameObject, index: number) {
        this.addChild(gameObject);
        const currentIndex = this.children.indexOf(gameObject);
        ArrayHelper.move(this.children, currentIndex, index);
        ArrayHelper.move(this.container.children, currentIndex, index);
    }

    public addChildRelativeTo(child: GameObject, gameobject: GameObject, distance: number) {
        const index = MathHelper.clamp(this.children.indexOf(gameobject) + distance, 0, this.children.length - 1);
        this.addChildAt(child, index);
    }

    public removeChild(gameObject: GameObject): void {
        if (!this.hasChild(gameObject)) return;

        gameObject.parent = null;

        const index = this.children.indexOf(gameObject);
        if (index >= 0) this.children.splice(index, 1);

        this.container.removeChild(gameObject.container);

        this.emit(GameObjectEvent.CHILD_REMOVED, gameObject);
    }

    public setDisplayParent(displayParent: GameObject | null): void {
        if (!displayParent) {
            this.removeDisplayParent();
            return;
        }

        if (this.container.displayParent === displayParent.container) return;

        this.container.setDisplayParent(displayParent.container);
        this.emit(GameObjectEvent.DISPLAY_PARENT_CHANGE, displayParent);
    }

    public removeDisplayParent(): void {
        if (!this.container.displayParent) return;

        this.container.removeDisplayParent();
        this.emit(GameObjectEvent.DISPLAY_PARENT_CHANGE, null);
    }

    public hasChild(gameObject: GameObject): boolean {
        return this.children.indexOf(gameObject) !== -1;
    }

    public find(path: string): GameObject {
        const names: Array<string> = path.split('/');
        return names.reduce((previous: GameObject, name: string) => {
            if (previous) {
                return previous.getChildByName(name);
            }
        }, this as GameObject);
    }

    public findChildByName(name: string): GameObject {
        if (this.name === name) return this;

        let target: GameObject;
        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            target = child.findChildByName(name);
            if (target) return target;
        }
    }

    public addComponent<T extends Component>(
        ctor: { new (): T },
        params?: ComponentAttachParams<T>
    ): T {
        let component = this.getComponent(ctor);

        if (component) {
            component.update(params);
        }
        else {
            component = new ctor();

            this.components.push(component);

            this.componentTypeMap.set(ctor, component);

            component.attach(this, params);
        }
        return component;
    }

    public updateComponent<T extends Component>(
        ctor: { new (): T },
        params?: ComponentUpdateParams<T>
    ): void {
        this.getComponent(ctor)?.update(params);
    }

    public getComponent<T extends Component>(ctor: { new (): T }): T {
        return this.componentTypeMap.get(ctor) as T;
    }

    public hasComponent<T extends Component>(ctor: { new (): T }): boolean {
        return !!this.getComponent<T>(ctor);
    }

    public hasComponents(componentTypes: { new (): Component }[]): boolean {
        return componentTypes.every(ctor => this.hasComponent(ctor));
    }

    public removeComponent<T extends Component>(ctor: { new (): T }): void {
        const component: T = this.getComponent(ctor);
        if (component) {
            const index = this.components.indexOf(component);
            if (index >= 0) this.components.splice(index, 1);

            this.componentTypeMap.delete(ctor);
            component.detach();
        }
    }

    public removeAllComponents(): void {
        this.componentTypeMap.forEach((_, ctor) => this.removeComponent(ctor));
    }

    /**
     * Search for components on children and so on.
     * @param ctor The type of Component to retrieve.
     */
    public getComponentsInChildren<T extends Component>(
        ctor: Constructor<T>
    ): Array<T> {
        const component: T = this.getComponent(ctor);
        return this.children.reduce(
            (acc: T[], val: GameObject) => {
                return acc.concat(val.getComponentsInChildren<T>(ctor));
            },
            component ? [component] : []
        );
    }

    public setContainer(container: T): void {
        this.container = container;
        this.components.forEach(component => {
            component.update({ container });
        })
    }

    /**
     * Unique identifier required for Map keys.
     */
    public toString(): string {
        if (this.name) {
            return `${this.name}.${this.id}`;
        }

        return this.id;
    }

    public emit(event: string, ...args: any[]): void {
        this.container.emit(event, ...args);
    }

    public off(
        event: string,
        fn?: (...args: any[]) => any,
        context?: any
    ): void {
        this.container.off(event, fn, context);
    }

    public on(event: string, fn: (...args: any[]) => any, context?: any): void {
        this.container.on(event, fn, context);
    }
    public once(
        event: string,
        fn: (...args: any[]) => any,
        context?: any
    ): void {
        this.container.once(event, fn, context);
    }
}
